import { isQuickRepliesMessage } from '@enuan/chat__utils';
import { styled } from '@mui/material/styles';
import classNames from 'classnames';
import React, { useCallback, useContext, useState, type JSX } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { ReactComponent as CloseIcon } from '@/assets/close.svg';
import { frameStateContext } from '../../contexts/frame/context';
import { genericMessages } from '../../generic.i18n';
import {
	getLastUnreadPopupMessage,
	messagesSelectors,
} from '../../reduxStore/selectors/messages.selector';
import { messageContext } from '../message/message.context';
import { QuickRepliesMessage } from './quick-replies/QuickRepliesMessage';
import { TextMessage } from './text/TextMessage';
import { useEnuanLocalStorage } from 'src/hooks/useEnuanLocalStorage';
import { IClientMessage, IClientMessageMeta } from 'src/interfaces';
import { messagesSlice } from 'src/reduxStore';

type IPopupWrapperProps = JSX.IntrinsicElements['div'] & {
	onClose?: () => void;
};

type IPopupMessage = Omit<IPopupWrapperProps, 'children'>;

export const PopupMessage = styled(
	({
		onClose,
		className,
		...props
	}: IPopupMessage & { className?: string }) => {
		const intl = useIntl();
		const closeText = intl.formatMessage(genericMessages.cancel);
		const [isOpenPopup, setIsOpenPopup] = useState<boolean>(true);
		const {
			isOpen: { isOpen },
			toggleChat,
		} = useContext(frameStateContext);
		const message = useSelector(getLastUnreadPopupMessage);
		const { toggleRead } = useToggleRead(message?.client_id);

		if (!message || isOpen || !isOpenPopup) return null;

		return (
			<div className={classNames('popup-wrapper', className)} {...props}>
				<button
					type="button"
					className="close"
					onClick={() => {
						setIsOpenPopup(false);
						onClose?.();
						toggleRead();
					}}
					title={closeText}
				>
					<CloseIcon className="icon close" />
				</button>
				<messageContext.Provider value={{ message }}>
					<PopupMessageContent
						message={message}
						toggleChat={toggleChat}
						isOpen={isOpen}
					/>
				</messageContext.Provider>
			</div>
		);
	},
	{
		label: 'PopupMessage',
		target: 'PopupMessage-root',
	},
)({
	display: 'flex',
	flexDirection: 'column',
	pointerEvents: 'all',
	maxWidth: '39rem',
	position: 'relative',
	'& > .close': {
		position: 'absolute',
		right: 'calc(-2em * 0.5)',
		top: 'calc(-2em * 0.5)',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		borderRadius: '50%',
		width: '2em',
		height: '2em',
		backgroundColor: 'var(--popup__close_background)',
		'& .st0': {
			fill: 'var(--popup__close_color)',
		},
	},
	'& .message--popup': {
		marginTop: '1em',
		gridTemplateColumns: 'none',
		padding: 0,
		'& .quick-replies__option': {
			whiteSpace: 'nowrap',
			overflow: 'hidden',
			textOverflow: 'ellipsis',
		},
		'& .message__balloon': {
			justifySelf: 'stretch',
		},
		'& .message__footer': {
			display: 'none',
		},
		'& .message__extra': {
			marginTop: '1em',
		},
	},
});

const useToggleRead = (client_id: IClientMessage['client_id'] = '') => {
	const dispatch = useDispatch();

	const { updateStorageTimestamp } = useEnuanLocalStorage();

	const { timestamp, _meta: { read: isRead = undefined } = {} } =
		useSelector((state) => messagesSelectors.selectById(state, client_id)) ??
		{};

	const toggleRead = useCallback(
		(read: IClientMessageMeta['read'] = !isRead) => {
			if (!client_id) return;
			dispatch(
				messagesSlice.actions.toggleRead({
					client_id,
					read,
				}),
			);
			if (read && timestamp) {
				updateStorageTimestamp(timestamp);
			}
		},
		[dispatch, client_id, timestamp, updateStorageTimestamp, isRead],
	);

	return { isRead, toggleRead };
};

type PopupMessageContentProps = {
	message: IClientMessage;
	toggleChat: (open: boolean) => void;
	isOpen: boolean;
};

const PopupMessageContent = ({
	message,
	toggleChat,
	isOpen,
}: PopupMessageContentProps) => {
	if (isQuickRepliesMessage(message))
		return (
			<QuickRepliesMessage
				className="message--popup"
				onQuickReplies={() => toggleChat(true)}
				getRead={() => !isOpen}
			/>
		);
	return <TextMessage className="message--popup" getRead={() => !isOpen} />;
};
